import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Phone,
  Bot,
  Calendar,
  MessageSquare,
  User,
  Clock,
} from 'lucide-react';
import { adminApi } from '../../services/adminApi';

export default function ConversationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchConversation = async () => {
      try {
        setIsLoading(true);
        const response = await adminApi.getConversation(id);
        setConversation(response.conversation);
        setMessages(response.messages || []);
      } catch (err) {
        console.error('Failed to fetch conversation:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchConversation();
  }, [id]);

  useEffect(() => {
    // Scroll to bottom when messages load
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatMessageTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const isThisYear = date.getFullYear() === now.getFullYear();

    if (isToday) {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      });
    } else if (isThisYear) {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 lg:p-8 max-w-4xl mx-auto">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-bg-secondary rounded w-1/4"></div>
          <div className="card p-6">
            <div className="h-96 bg-bg-secondary rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="p-6 lg:p-8 max-w-4xl mx-auto text-center">
        <MessageSquare className="w-16 h-16 text-text-muted mx-auto mb-4" />
        <p className="text-text-muted mb-4">Conversation not found</p>
        <button onClick={() => navigate('/conversations')} className="btn-primary">
          Back to Conversations
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-text-secondary hover:text-text-primary mb-6"
      >
        <ArrowLeft className="w-5 h-5" />
        Back
      </button>

      {/* Conversation Header */}
      <div className="card p-6 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
              <Phone className="w-7 h-7 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-text-primary">
                {conversation.senderName || conversation.senderPhone || 'Unknown Number'}
              </h1>
              {conversation.senderName && conversation.senderPhone && (
                <p className="text-sm text-text-muted flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  {conversation.senderPhone}
                </p>
              )}
              {conversation.agentName && (
                <Link
                  to={`/agents/${conversation.agentId}`}
                  className="text-sm text-primary hover:underline flex items-center gap-1 mt-1"
                >
                  <Bot className="w-4 h-4" />
                  {conversation.agentName}
                </Link>
              )}
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-text-muted flex items-center gap-1 justify-end">
              <MessageSquare className="w-4 h-4" />
              {conversation.messageCount || messages.length} messages
            </p>
            <p className="text-sm text-text-muted mt-1 flex items-center gap-1 justify-end">
              <Calendar className="w-4 h-4" />
              Started {formatDate(conversation.createdAt)}
            </p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="card">
        <div className="p-4 border-b border-border">
          <h2 className="font-semibold text-text-primary">Chat Messages</h2>
        </div>
        <div className="p-4 h-[500px] overflow-y-auto bg-bg-secondary/30">
          {messages.length === 0 ? (
            <div className="flex items-center justify-center h-full text-text-muted">
              <p>No messages in this conversation</p>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((message, index) => {
                const isUser = message.role === 'user';
                const isAssistant = message.role === 'assistant';

                return (
                  <div
                    key={message.id || index}
                    className={`flex ${isUser ? 'justify-start' : 'justify-end'}`}
                  >
                    <div
                      className={`max-w-[75%] rounded-2xl px-4 py-3 ${
                        isUser
                          ? 'bg-bg-secondary rounded-bl-none'
                          : 'bg-primary text-white rounded-br-none'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        {isUser ? (
                          <User className="w-4 h-4 text-text-muted" />
                        ) : (
                          <Bot className="w-4 h-4 text-white/70" />
                        )}
                        <span
                          className={`text-xs font-medium ${
                            isUser ? 'text-text-muted' : 'text-white/70'
                          }`}
                        >
                          {isUser ? 'Customer' : 'AI Agent'}
                        </span>
                        <span
                          className={`text-xs ${
                            isUser ? 'text-text-muted' : 'text-white/60'
                          }`}
                        >
                          {formatMessageTime(message.timestamp)}
                        </span>
                      </div>
                      <p
                        className={`text-sm whitespace-pre-wrap ${
                          isUser ? 'text-text-primary' : 'text-white'
                        }`}
                      >
                        {message.content}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Conversation Info Footer */}
        <div className="p-4 border-t border-border bg-bg-secondary/30">
          <div className="flex items-center justify-between text-sm text-text-muted">
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              Last updated: {formatDate(conversation.updatedAt)}
            </span>
            <Link
              to={`/conversations?agent_id=${conversation.agentId}`}
              className="text-primary hover:underline"
            >
              View all conversations for this agent
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
